import { getQueryAttribution } from '@/lib/brandQuery';
import { canMeasure, sanitizeMeasurement } from '@/lib/trackingPrivacy';
// Google Tag Manager & Analytics utilities
// This file provides functions to track events and conversions


// GTM Configuration - All tracking is managed through GTM
export const GTM_CONFIG = {
  containerId: 'GTM-T69CRTDT',
  ga4MeasurementId: 'G-9LM236SDT6',
  googleAdsId: 'AW-17797695183',
} as const;

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
    gtag: (...args: unknown[]) => void;
  }
}

// Debug mode - enable in development or with URL param ?gtm_debug=true
const isDebugMode = () => {
  if (typeof window === 'undefined') return false;
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('gtm_debug') === 'true' || import.meta.env.DEV;
};

// Debug logger for GTM events
const logGTMEvent = (eventName: string, data: Record<string, unknown>) => {
  if (isDebugMode()) {
    console.group(`🏷️ GTM Event: ${eventName}`);
    console.log('Container ID:', GTM_CONFIG.containerId);
    console.log('Data:', data);
    console.log('Timestamp:', new Date().toISOString());
    console.groupEnd();
  }
};

// Initialize dataLayer if not exists
export const initDataLayer = () => {
  window.dataLayer = window.dataLayer || [];
  if (isDebugMode()) {
    console.log('🏷️ GTM DataLayer initialized', {
      containerId: GTM_CONFIG.containerId,
      ga4Id: GTM_CONFIG.ga4MeasurementId,
      adsId: GTM_CONFIG.googleAdsId,
      dataLayer: window.dataLayer,
    });
  }
};

// Collect standard page context params attached to every event
const SUPPORTED_GTM_LANGS = ['en', 'tr', 'nl', 'fr', 'ar'] as const;

/**
 * Resolve the active site language from the URL prefix first (/tr/..., /nl/...),
 * falling back to <html lang>. Every event carries this so GA4/Ads reporting can
 * be segmented per language without extra instrumentation in components.
 */
export const resolveEventLanguage = (): string => {
  if (typeof window === 'undefined') return 'en';
  const match = window.location.pathname.match(/^\/([a-z]{2})(?=\/|$)/i);
  const fromPath = match ? match[1].toLowerCase() : '';
  if ((SUPPORTED_GTM_LANGS as readonly string[]).includes(fromPath)) return fromPath;
  const htmlLang =
    typeof document !== 'undefined'
      ? (document.documentElement?.lang || '').slice(0, 2).toLowerCase()
      : '';
  if ((SUPPORTED_GTM_LANGS as readonly string[]).includes(htmlLang)) return htmlLang;
  return 'en';
};

const getPageContext = (): Record<string, string> => {
  if (typeof window === 'undefined') return {};
  const attribution = getQueryAttribution();
  return {
    // Brand-query attribution: lets GA4 / Google Ads segment brand vs non-brand
    // demand and match it back to CRM leads carrying the same values.
    query_type: attribution.query_type,
    channel_group: attribution.channel_group,
    page_path: window.location.pathname,
    page_location: window.location.origin + window.location.pathname,
    page_title: 'Hair Art Clinics',
    language: resolveEventLanguage(),
  };
};


// ============================================================
// EVENT PARAMETER SCHEMA & VALIDATION
// ============================================================
// Required parameters per event. Every tracked event MUST include
// these keys with a non-empty value before being pushed to GTM.
// If a required key is missing/empty, the event is auto-repaired
// with a sentinel value ('unknown') and a warning is logged so we
// can catch instrumentation gaps in DebugView / dev console.

const GLOBAL_REQUIRED_PARAMS = ['page_path', 'page_title', 'language', 'query_type', 'channel_group'] as const;

const EVENT_SCHEMA: Record<string, readonly string[]> = {
  page_view: ['page_path', 'page_title'],
  form_submission: ['form_name', 'form_location'],
  form_submit: ['form_name', 'form_location'],
  form_abandonment: ['form_type'],
  booking_conversion: ['conversion_type'],
  contact_conversion: ['conversion_type'],
  hair_analysis_conversion: ['conversion_type'],
  whatsapp_click: ['click_location'],
  phone_click: ['click_location'],
  cta_click: ['button_name', 'click_location'],
  google_ads_whatsapp_click: ['click_location'],
  google_ads_phone_click: ['click_location'],
  treatment_view: ['treatment_slug', 'treatment_name'],
  blog_view: ['post_slug', 'post_title'],
  pdf_download: ['pdf_name'],
  scroll_depth: ['scroll_percentage'],
  engagement_time: ['time_on_page'],
  hero_cta_click: ['button_name', 'click_location', 'destination'],
  booking_form_submit: ['form_name', 'form_location', 'conversion_type'],
  footer_contact_click: ['channel', 'click_location', 'contact_target'],
  lead_form_submit: ['form_name', 'form_location'],
  booking_started: ['form_name', 'form_location'],
  booking_completed: ['form_name', 'form_location'],
  consultation_request: ['form_name', 'form_location'],
  price_request: ['form_name', 'form_location'],
  thank_you_page_view: ['form_location', 'conversion_type'],
};


const SENTINEL = 'unknown';

const isEmpty = (v: unknown): boolean =>
  v === undefined ||
  v === null ||
  (typeof v === 'string' && v.trim() === '') ||
  (typeof v === 'number' && Number.isNaN(v));

export interface ValidationResult {
  valid: boolean;
  missing: string[];
  repaired: Record<string, unknown>;
}

// Validate an event payload against its schema. Returns a repaired
// payload where missing required keys are filled with SENTINEL so
// GA4 never receives (not set) for required custom dimensions.
export const validateEventPayload = (
  payload: Record<string, unknown>
): ValidationResult => {
  const eventName = (payload.event as string) || 'unknown';
  const schemaKeys = EVENT_SCHEMA[eventName] ?? [];
  const requiredKeys = Array.from(new Set([...GLOBAL_REQUIRED_PARAMS, ...schemaKeys]));

  const repaired = { ...payload };
  const missing: string[] = [];

  for (const key of requiredKeys) {
    if (isEmpty(repaired[key])) {
      missing.push(key);
      repaired[key] = SENTINEL;
    }
  }

  return { valid: missing.length === 0, missing, repaired };
};

// Push event to dataLayer for GTM (auto-enriched with page context
// and validated against the event schema).
export const pushToDataLayer = (event: Record<string, unknown>) => {
  if (!canMeasure('analytics')) return;
  if (/remarketing|high_intent|returning_visitor|enhanced_conversion/.test(String(event.event))) return;
  initDataLayer();
  const ctx = getPageContext();
  const enriched: Record<string, unknown> = { ...ctx, ...event };
  // Never let an undefined/empty per-call language wipe the resolved page language.
  if (isEmpty(enriched.language) && ctx.language) enriched.language = ctx.language;
  const { valid, missing, repaired } = validateEventPayload(enriched);

  if (!valid && isDebugMode()) {
    console.warn(
      `⚠️ GTM event "${repaired.event}" eksik zorunlu parametre(ler): ${missing.join(', ')} — '${SENTINEL}' ile dolduruldu.`,
      { original: enriched, repaired }
    );
  }

  const safe = sanitizeMeasurement(repaired);
  logGTMEvent((safe.event as string) || 'unknown', safe);
  window.dataLayer.push(safe);
};

// Track page view
export const trackPageView = (pagePath: string, pageTitle: string) => {
  pushToDataLayer({
    event: 'page_view',
    page_path: pagePath,
    page_title: 'Hair Art Clinics',
  });
};

// Track form submission (for conversion tracking)
export const trackFormSubmission = (formName: string, formData?: Record<string, unknown>) => {
  pushToDataLayer({
    event: 'form_submission',
    form_name: formName || 'unknown_form',
    form_location:
      typeof window !== 'undefined' && window.location.pathname
        ? window.location.pathname
        : 'unknown',
    form_data: formData,
  });
};

// Track booking/consultation request (primary conversion)
export const trackBookingConversion = (treatmentType?: string, value?: number) => {
  pushToDataLayer({
    event: 'booking_conversion',
    conversion_type: 'booking_request',
    treatment_type: treatmentType,
    conversion_value: value,
    currency: 'EUR',
  });
};

// Track contact form submission
export const trackContactConversion = (source?: string) => {
  pushToDataLayer({
    event: 'contact_conversion',
    conversion_type: 'contact_form',
    source: source,
  });
};

// Track hair analysis completion
export const trackHairAnalysisConversion = () => {
  pushToDataLayer({
    event: 'hair_analysis_conversion',
    conversion_type: 'hair_analysis',
  });
};

// Track PDF download (lead magnet)
export const trackPDFDownload = (pdfName: string) => {
  pushToDataLayer({
    event: 'pdf_download',
    pdf_name: pdfName,
  });
};

// Track WhatsApp click
export const trackWhatsAppClick = (location?: string, phoneNumber?: string) => {
  pushToDataLayer({
    event: 'whatsapp_click',
    click_location: location ?? 'unknown',
    link_url: phoneNumber ? `https://wa.me/${phoneNumber.replace(/\D/g, '')}` : undefined,
    outbound: true,
  });
};

// Track phone call click
export const trackPhoneClick = (location?: string, phoneNumber?: string) => {
  pushToDataLayer({
    event: 'phone_click',
    click_location: location ?? 'unknown',
    link_url: phoneNumber ? `tel:${phoneNumber}` : undefined,
    outbound: true,
  });
};

// Track CTA button click
export const trackCTAClick = (buttonName: string, location?: string) => {
  pushToDataLayer({
    event: 'cta_click',
    button_name: buttonName,
    click_location: location ?? 'unknown',
  });
};

// ============================================================
// SPECIALIZED CONVERSION EVENTS (Hero, Booking, Footer)
// ============================================================
// These wrappers guarantee GA4 required custom dimensions never
// go out empty for our three highest-value interaction points.

// Hero section CTA (e.g. "Ücretsiz Danışma", "WhatsApp'tan Yaz")
export const trackHeroCtaClick = (
  buttonName: string,
  destination: string,
  location = 'hero'
) => {
  pushToDataLayer({
    event: 'hero_cta_click',
    button_name: buttonName || 'unknown_button',
    click_location: location || 'hero',
    destination: destination || 'unknown',
  });
};

// Booking / consultation form submission (primary conversion)
export const trackBookingFormSubmit = (
  formName: string,
  treatmentType?: string,
  value?: number
) => {
  pushToDataLayer({
    event: 'booking_form_submit',
    form_name: formName || 'booking_form',
    form_location:
      typeof window !== 'undefined' && window.location.pathname
        ? window.location.pathname
        : 'unknown',
    conversion_type: 'booking_request',
    treatment_type: treatmentType,
    conversion_value: value,
    currency: 'EUR',
  });
};

// Footer contact channel click (WhatsApp / phone / email)
export type FooterContactChannel = 'whatsapp' | 'phone' | 'email';

export const trackFooterContactClick = (
  channel: FooterContactChannel,
  target: string,
  location = 'footer'
) => {
  const linkUrl =
    channel === 'whatsapp'
      ? `https://wa.me/${(target || '').replace(/\D/g, '')}`
      : channel === 'phone'
      ? `tel:${target}`
      : `mailto:${target}`;

  pushToDataLayer({
    event: 'footer_contact_click',
    channel,
    click_location: location || 'footer',
    contact_target: target || 'unknown',
    link_url: linkUrl,
    outbound: true,
  });
};



// Track treatment page view (for remarketing audiences)
export const trackTreatmentView = (treatmentSlug: string, treatmentName: string) => {
  pushToDataLayer({
    event: 'treatment_view',
    treatment_slug: treatmentSlug,
    treatment_name: treatmentName,
  });
};

// Track blog post view
export const trackBlogView = (postSlug: string, postTitle: string, category?: string) => {
  pushToDataLayer({
    event: 'blog_view',
    post_slug: postSlug,
    post_title: postTitle,
    post_category: category,
  });
};

// Track gallery view
export const trackGalleryView = (galleryType?: string) => {
  pushToDataLayer({
    event: 'gallery_view',
    gallery_type: galleryType,
  });
};

// Track exit intent popup interaction
export const trackExitIntent = (action: 'shown' | 'closed' | 'submitted') => {
  pushToDataLayer({
    event: 'exit_intent',
    exit_intent_action: action,
  });
};

// Track pricing popup view
export const trackPricingView = () => {
  pushToDataLayer({
    event: 'pricing_view',
  });
};

// Track candidacy test completion
export const trackCandidacyTest = (result: string, score?: number) => {
  pushToDataLayer({
    event: 'candidacy_test_complete',
    test_result: result,
    test_score: score,
  });
};

// Track scroll depth
export const trackScrollDepth = (depth: number) => {
  pushToDataLayer({
    event: 'scroll_depth',
    scroll_percentage: depth,
  });
};

// Track user engagement time
export const trackEngagementTime = (seconds: number) => {
  pushToDataLayer({
    event: 'engagement_time',
    time_on_page: seconds,
  });
};

// Enhanced ecommerce - Add to cart equivalent (consultation selected)
export const trackConsultationSelected = (treatmentType: string) => {
  pushToDataLayer({
    event: 'add_to_cart',
    ecommerce: {
      items: [{
        item_name: treatmentType,
        item_category: 'Hair Transplant',
        quantity: 1,
      }],
    },
  });
};

// Enhanced ecommerce - Begin checkout equivalent (booking started)
export const trackBookingStarted = (treatmentType: string) => {
  pushToDataLayer({
    event: 'begin_checkout',
    ecommerce: {
      items: [{
        item_name: treatmentType,
        item_category: 'Hair Transplant',
        quantity: 1,
      }],
    },
  });
};

// Google Ads Conversion ID
const GOOGLE_ADS_CONVERSION_ID = 'AW-17797695183';

// Google Ads conversion labels.
// Only labels issued by Google Ads are valid; placeholder labels are disabled (null)
// so that no invalid conversion hits are sent.
export const GOOGLE_ADS_CONVERSIONS = {
  BOOK_APPOINTMENT: `${GOOGLE_ADS_CONVERSION_ID}/4rYECPHAneYcEM-NzaZC`,
  BOOKING_FORM: `${GOOGLE_ADS_CONVERSION_ID}/4rYECPHAneYcEM-NzaZC`,
  CONTACT_FORM: null,
  WHATSAPP_CLICK: null,
  PHONE_CLICK: null,
  HAIR_ANALYSIS: null,
  PDF_DOWNLOAD: null,
} as const;

// Session-scoped conversion de-duplication (mirrors src/lib/conversionContext.ts;
// kept inline so this module stays dependency-free for tag transpilation).
const FIRED_CONVERSIONS_KEY = 'hac_fired_conversions';
const markConversionFired = (key: string): boolean => {
  if (!key) return false;
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return true;
    const raw = window.sessionStorage.getItem(FIRED_CONVERSIONS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    const fired: string[] = Array.isArray(parsed) ? parsed.filter((i: unknown) => typeof i === 'string') as string[] : [];
    if (fired.includes(key)) return false;
    window.sessionStorage.setItem(FIRED_CONVERSIONS_KEY, JSON.stringify([...fired.slice(-49), key]));
    return true;
  } catch {
    return true;
  }
};

export interface GoogleAdsConversionOptions {
  value?: number;
  currency?: string;
  transactionId?: string;
  /** When set, the conversion fires at most once per browser session for this key. */
  dedupeKey?: string;
}

// Google Ads specific conversion (call after successful form submission)
export const trackGoogleAdsConversion = (
  conversionLabel: string | null,
  valueOrOptions: number | GoogleAdsConversionOptions = {}
) => {
  if (!conversionLabel || !canMeasure('marketing')) return false;
  const options: GoogleAdsConversionOptions =
    typeof valueOrOptions === 'number' ? { value: valueOrOptions } : (valueOrOptions ?? {});
  const value = typeof options.value === 'number' && Number.isFinite(options.value) ? options.value : undefined;
  const currency = options.currency && /^[A-Za-z]{3}$/.test(options.currency)
    ? options.currency.toUpperCase()
    : 'EUR';

  if (options.dedupeKey && !markConversionFired(options.dedupeKey)) {
    if (isDebugMode()) {
      console.warn('⏭️ Google Ads conversion skipped (duplicate):', options.dedupeKey);
    }
    return false;
  }

  if (typeof window.gtag === 'function') {
    if (isDebugMode()) {
      console.group('🎯 Google Ads Conversion');
      console.log('Label:', conversionLabel);
      console.log('Value:', value, currency);
      console.log('Transaction ID:', options.transactionId);
      console.groupEnd();
    }
    const payload: Record<string, unknown> = {
      send_to: conversionLabel,
      ...(value !== undefined ? { value, currency } : {}),
    };
    if (options.transactionId) payload.transaction_id = options.transactionId;
    window.gtag('event', 'conversion', payload);
    return true;
  }
  if (isDebugMode()) {
    console.warn('⚠️ gtag function not available - Google Ads conversion not tracked');
  }
  return false;
};



// Track contact form conversion for Google Ads
export const trackGoogleAdsContactForm = (treatmentType?: string) => {
  trackGoogleAdsConversion(GOOGLE_ADS_CONVERSIONS.CONTACT_FORM);
  pushToDataLayer({
    event: 'google_ads_contact_form',
    treatment_type: treatmentType,
  });
};

/**
 * Shared transaction id for the booking journey.
 * The booking form and the thank-you page report the SAME Google Ads
 * conversion label, so both must dedupe against one transaction id.
 * Reads (or lazily creates) the conversion context written by the forms.
 */
const CONVERSION_CONTEXT_KEY = 'hac_conversion_context';
const resolveBookingTransactionId = (value?: number): string => {
  const fallback = `tx_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return fallback;
    const raw = window.sessionStorage.getItem(CONVERSION_CONTEXT_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { transactionId?: string; createdAt?: number };
      if (parsed?.transactionId && Date.now() - (parsed.createdAt ?? 0) < 60 * 60 * 1000) {
        return parsed.transactionId;
      }
    }
    window.sessionStorage.setItem(
      CONVERSION_CONTEXT_KEY,
      JSON.stringify({
        value: typeof value === 'number' ? value : 1.0,
        currency: 'EUR',
        transactionId: fallback,
        formType: 'booking',
        createdAt: Date.now(),
      })
    );
  } catch {
    /* storage blocked - fall back to a one-off id */
  }
  return fallback;
};

// Track booking form conversion for Google Ads
export const trackGoogleAdsBookingForm = (treatmentType?: string, value?: number) => {
  const transactionId = resolveBookingTransactionId(value);
  trackGoogleAdsConversion(GOOGLE_ADS_CONVERSIONS.BOOKING_FORM, {
    value,
    currency: 'EUR',
    transactionId,
    dedupeKey: `book_appointment:${transactionId}`,
  });
  pushToDataLayer({
    event: 'google_ads_booking_form',
    treatment_type: treatmentType,
    conversion_value: value,
    transaction_id: transactionId,
  });
};


// Track WhatsApp click conversion for Google Ads
export const trackGoogleAdsWhatsAppClick = (location?: string) => {
  trackGoogleAdsConversion(GOOGLE_ADS_CONVERSIONS.WHATSAPP_CLICK);
  pushToDataLayer({
    event: 'google_ads_whatsapp_click',
    click_location: location,
  });
};

// Track phone call click conversion for Google Ads
export const trackGoogleAdsPhoneClick = (location?: string) => {
  trackGoogleAdsConversion(GOOGLE_ADS_CONVERSIONS.PHONE_CLICK);
  pushToDataLayer({
    event: 'google_ads_phone_click',
    click_location: location,
  });
};

// Track hair analysis conversion for Google Ads
export const trackGoogleAdsHairAnalysis = () => {
  trackGoogleAdsConversion(GOOGLE_ADS_CONVERSIONS.HAIR_ANALYSIS);
  pushToDataLayer({
    event: 'google_ads_hair_analysis',
  });
};

// Generic conversion tracking
export const trackConversion = (eventName: string, value?: number) => {
  pushToDataLayer({
    event: eventName,
    conversion_value: value,
  });
};

// ==========================================
// REMARKETING AUDIENCES
// ==========================================

// Remarketing audience types
export const REMARKETING_AUDIENCES = {
  TREATMENT_VIEWER: 'treatment_viewer',
  FORM_ABANDONER: 'form_abandoner',
  HIGH_INTENT: 'high_intent',
  RETURNING_VISITOR: 'returning_visitor',
  PDF_DOWNLOADER: 'pdf_downloader',
} as const;

// Track treatment page view for remarketing (users who view but don't convert)
export const trackRemarketingTreatmentView = (treatmentSlug: string, treatmentName: string) => {
  // Do not construct audiences from health interests.
  void treatmentSlug; void treatmentName;
};

// Track form abandonment for remarketing
export const trackFormAbandonment = (formType: string, treatmentType?: string) => {
  pushToDataLayer({
    event: 'form_abandonment',
    audience_type: REMARKETING_AUDIENCES.FORM_ABANDONER,
    form_type: formType,
    treatment_type: treatmentType,
  });
};

// Track high intent users (multiple page visits, long session)
export const trackHighIntentUser = (pageVisits: number, sessionDuration: number) => {
  if (pageVisits >= 3 || sessionDuration >= 180) {
    pushToDataLayer({
      event: 'high_intent_user',
      audience_type: REMARKETING_AUDIENCES.HIGH_INTENT,
      page_visits: pageVisits,
      session_duration: sessionDuration,
    });
  }
};

// Track returning visitors
export const trackReturningVisitor = () => {
  if (!canMeasure('marketing')) return;
  const visitCount = parseInt(localStorage.getItem('visit_count') || '0', 10) + 1;
  localStorage.setItem('visit_count', visitCount.toString());
  
  if (visitCount > 1) {
    pushToDataLayer({
      event: 'returning_visitor',
      audience_type: REMARKETING_AUDIENCES.RETURNING_VISITOR,
      visit_count: visitCount,
    });
  }
};

// Build remarketing audience segments
export const buildRemarketingSegment = (segmentData: {
  viewedTreatments?: string[];
  formStarted?: boolean;
  formCompleted?: boolean;
  pdfDownloaded?: boolean;
  timeOnSite?: number;
}) => {
  const segment: string[] = [];
  
  if (segmentData.viewedTreatments && segmentData.viewedTreatments.length > 0) {
    segment.push('treatment_interested');
  }
  if (segmentData.formStarted && !segmentData.formCompleted) {
    segment.push('form_abandoner');
  }
  if (segmentData.pdfDownloaded) {
    segment.push('content_engaged');
  }
  if (segmentData.timeOnSite && segmentData.timeOnSite > 120) {
    segment.push('high_engagement');
  }
  
  if (segment.length > 0) {
    pushToDataLayer({
      event: 'remarketing_segment',
      user_segments: segment,
    });
  }
  
  return segment;
};

// ==========================================
// ENHANCED CONVERSIONS
// ==========================================

// Enhanced Conversion data interface
interface EnhancedConversionData {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  address?: {
    street?: string;
    city?: string;
    region?: string;
    postalCode?: string;
    country?: string;
  };
}

// Track Enhanced Conversion with hashed user data
export const trackEnhancedConversion = async (
  conversionLabel: string,
  userData: EnhancedConversionData,
  value?: number
) => {
  // Patient enquiries are excluded from enhanced customer matching.
  void conversionLabel; void userData; void value;
};

// Enhanced Conversion for Contact Form
export const trackEnhancedContactConversion = async (userData: {
  email: string;
  name?: string;
  phone?: string;
}) => {
  const nameParts = (userData.name || '').split(' ');
  await trackEnhancedConversion(
    GOOGLE_ADS_CONVERSIONS.CONTACT_FORM,
    {
      email: userData.email,
      phone: userData.phone,
      firstName: nameParts[0],
      lastName: nameParts.slice(1).join(' '),
    }
  );
};

// Enhanced Conversion for Booking Form
export const trackEnhancedBookingConversion = async (
  userData: {
    email: string;
    name?: string;
    phone?: string;
  },
  value?: number
) => {
  const nameParts = (userData.name || '').split(' ');
  await trackEnhancedConversion(
    GOOGLE_ADS_CONVERSIONS.BOOKING_FORM,
    {
      email: userData.email,
      phone: userData.phone,
      firstName: nameParts[0],
      lastName: nameParts.slice(1).join(' '),
    },
    value
  );
};

// Enhanced Conversion for Hair Analysis
export const trackEnhancedHairAnalysisConversion = async (userData: {
  email?: string;
  phone?: string;
}) => {
  await trackEnhancedConversion(
    GOOGLE_ADS_CONVERSIONS.HAIR_ANALYSIS,
    {
      email: userData.email,
      phone: userData.phone,
    }
  );
};

// ============================================================
// GOOGLE ADS LAUNCH EVENTS (canonical names)
// ============================================================
// These wrappers expose the exact event names configured as GA4
// custom events / Google Ads conversion actions for the launch.
// Use them from public forms and CTAs so GTM triggers match 1:1.

type LeadContext = {
  form_name?: string;
  form_location?: string;
  treatment_type?: string;
  source?: string;
  language?: string;
  value?: number;
  currency?: string;
  lead_id?: string;
  duplicate?: boolean;
};

// Fired when a public lead form is successfully submitted (any channel).
export const trackLeadFormSubmit = (ctx: LeadContext = {}) => {
  pushToDataLayer({
    event: 'lead_form_submit',
    form_name: ctx.form_name || 'lead_form',
    form_location:
      ctx.form_location ||
      (typeof window !== 'undefined' ? window.location.pathname : 'unknown'),
    treatment_type: ctx.treatment_type,
    lead_source: ctx.source,
    language: ctx.language,
    lead_id: ctx.lead_id,
    duplicate: ctx.duplicate,
    conversion_value: ctx.value,
    currency: ctx.currency || 'EUR',
  });
};

/** Only the persistence bridge may call this after reading a successful server ID. */
export const trackPersistedLead = (id: string, duplicate: boolean, formType: string) => {
  if (duplicate || !id) return;
  if (canMeasure('analytics') && markConversionFired(`generate_lead:${id}`)) {
    pushToDataLayer({ event: 'generate_lead', form_name: formType, form_location: window.location.pathname });
  }
  if (['consultation', 'landing-page'].includes(formType)) {
    trackGoogleAdsConversion(GOOGLE_ADS_CONVERSIONS.BOOK_APPOINTMENT, { transactionId: id, dedupeKey: `persisted-booking:${id}` });
  }
};

// Fired when a booking flow starts (form opened / first field focused).
export const trackBookingStartedEvent = (ctx: LeadContext = {}) => {
  pushToDataLayer({
    event: 'booking_started',
    form_name: ctx.form_name || 'booking_form',
    form_location:
      ctx.form_location ||
      (typeof window !== 'undefined' ? window.location.pathname : 'unknown'),
    treatment_type: ctx.treatment_type,
    language: ctx.language,
  });
};

// Fired when a consultation booking is confirmed end-to-end.
export const trackBookingCompleted = (ctx: LeadContext = {}) => {
  pushToDataLayer({
    event: 'booking_completed',
    form_name: ctx.form_name || 'booking_form',
    form_location:
      ctx.form_location ||
      (typeof window !== 'undefined' ? window.location.pathname : 'unknown'),
    treatment_type: ctx.treatment_type,
    lead_source: ctx.source,
    language: ctx.language,
    lead_id: ctx.lead_id,
    duplicate: ctx.duplicate,
    conversion_value: ctx.value,
    currency: ctx.currency || 'EUR',
  });
};

// Fired when a visitor requests a consultation (softer intent than booking).
export const trackConsultationRequest = (ctx: LeadContext = {}) => {
  pushToDataLayer({
    event: 'consultation_request',
    form_name: ctx.form_name || 'consultation_request',
    form_location:
      ctx.form_location ||
      (typeof window !== 'undefined' ? window.location.pathname : 'unknown'),
    treatment_type: ctx.treatment_type,
    lead_source: ctx.source,
    language: ctx.language,
  });
};

// Fired when a visitor opens the pricing popup or asks for a price quote.
export const trackPriceRequest = (ctx: LeadContext & { channel?: string } = {}) => {
  pushToDataLayer({
    event: 'price_request',
    form_name: ctx.form_name || 'price_request',
    form_location:
      ctx.form_location ||
      (typeof window !== 'undefined' ? window.location.pathname : 'unknown'),
    treatment_type: ctx.treatment_type,
    channel: ctx.channel,
    language: ctx.language,
  });
};

// Fired on /thank-you page load, once per view.
export const trackThankYouPageView = (
  ctx: LeadContext & { conversion_type?: string } = {}
) => {
  pushToDataLayer({
    event: 'thank_you_page_view',
    form_location:
      ctx.form_location ||
      (typeof window !== 'undefined' ? window.location.pathname : '/thank-you'),
    conversion_type: ctx.conversion_type || 'lead',
    treatment_type: ctx.treatment_type,
    lead_source: ctx.source,
    language: ctx.language,
    lead_id: ctx.lead_id,
    conversion_value: ctx.value,
    currency: ctx.currency || 'EUR',
  });
};

